import BaseLayer from './_base/BaseLayer';
import _ from 'underscore';
import TransitStopsLayer from './TransitStopsLayer';

export default BaseLayer.extend({
  /**
   * Create a new RouteLayer instance
   * @memberOf module:Layers
   * @class RouteLayer
   * @classdesc The polyline layer to draw routes on map
   * @extends BaseLayer
   *  @param {object} options - The needed config to draw the routes on map
   */
  constructor(providedRoute, options = {}) {
    options.featureId = 'RouteLayer';
    options.source = {
      id: 'RouteLayer'
    };
    this.map = options.widget.map;
    this.providedRoute = providedRoute; //the data provided by api
    this.geoRoute = []; // the geometry route by steps
    this.optRoute = []; // the styles for each step
    this.styles = this._mergeStyles(options.routeStyles);
    this.routeModel = options.model;
    this.index = options.index;
    this.transitStops = options.transitStops;
    this.routeClickable = !_.isUndefined(options.routeClickable) ? options.routeClickable : true;
    this.routeHoverable = !_.isUndefined(options.routeHoverable) ? options.routeHoverable : true;
    if (this.styles.type !== 'agencyStyle' &&
      (this.styles.applyTo === 'all' || options.index < this.styles.applyTo)) {
      this.initialStyle = 'defaultStyle';
    } else {
      this.initialStyle = 'basicStyle';
    }
    if (this.styles.legend) {
      this.legend = [];
    }
    if (options.backgroundRoute) {
      this.backgroundRoute = [];
      if (this.routeModel.isSelected()) {
        this._createBackgroundRoute();
      }
    }
    BaseLayer.prototype.constructor.call(this, options);
  },
  /**
   * Initialize the route
   * @memberOf module:Layers.RouteLayer
   * @function
   */
  initialize() {
    this._createRoute();
  },
  _addElementToLegend(text, style) {
    if (this.legend) {
      this.legend.push({ text: text, type: 'Polyline', style: style });
    }
  },
  getLegend() {
    return this.legend;
  },
  //Combine external styles with default ones
  _mergeStyles(externalStyle) {
    let style = externalStyle;
    let internalStyle = this._defaultStyles();
    _.each(internalStyle, (value, name) => {
      if (_.isObject(value)) {
        style[name] = _.extend({}, internalStyle[name], externalStyle[name]);
      } else {
        //In order to add properties thar arent in external style
        style[name] = _.isUndefined(externalStyle[name]) ? internalStyle[name] : externalStyle[name];
      }
    });
    return style;
  },
  //Set default styles
  _defaultStyles(configStyle) {
    return {
      type: 'googleStyle', // 'uniqueColors', 'modesStyle', 'agencyStyle' or 'googleStyle'
      defaultStyle: {
        WALKING: {
          strokeOpacity: 0,
          strokeWeight: 0,
          icons: [{
            icon: {
              path: google.maps.SymbolPath.CIRCLE,
              fillColor: '#009afd',
              strokeColor: '#009afd',
              fillOpacity: 1,
              strokeOpacity: 1,
              scale: 2,
              zIndex: 20,
              iconClassLegend: 'circle'
            },
            offset: '0',
            repeat: '10px'
          }],
          zIndex: 20
        },
        TRANSIT: {
          strokeWeight: 2,
          strokeColor: '#0000CC',
          icons: false,
          zIndex: 20
        },
        DRIVING: {
          strokeWeight: 2,
          strokeColor: '#808080',
          icons: false,
          zIndex: 20
        },
        BICYCLING: {
          strokeWeight: 2,
          strokeColor: '#808080',
          icons: false,
          zIndex: 20
        },
        strokeWeight: 2,
        strokeColor: '#808080',
        icons: false,
        zIndex: 20
      },
      applyTo: 1, //apply the styles by default. Number indicates the first number routes, 'all', 'agency'
      basicStyle: {
        strokeWeight: 2,
        strokeColor: '#a0a0a0',
        strokeOpacity: 0.8,
        icons: false,
        zIndex: 10
      },
      highlightStyle: {
        strokeOpacity: 1,
        strokeWeight: 5,
        zIndex: 40
      },
      selectedStyle: {
        strokeWeight: 2,
        zIndex: 30
      },
      deselectClickingOut: true,
      legend: false
    };
  },
  //Create the route with its posible styles and properties
  _createRoute() {
    //there is one leg for each waypoint
    // presently, we only will have one leg
    let legs = this.providedRoute.legs;
    this.providedRoute.transitStops = [];
    _.each(legs, (leg) => {
      _.each(leg.steps, (step) => {
        this.optRoute.push(this._createOptStep(step));
        this._handleAgencyOption(step);
        this.providedRoute.transitStops.push(step);
      });
    });
    if (this.transitStops) {
      this.transitStopsLayer = new TransitStopsLayer(this.providedRoute.transitStops, {
        widget: this.widget
      });
    }
    this._createTransparentRoute();
  },
  //If is an array of agencies, check if this route belongs to one of them
  _handleAgencyOption(step) {
    let stepAgency = step.transit ? _.pluck(step.transit.line.agencies, 'name') : false;
    let agencies = this.styles.agencies || [];
    if (stepAgency) {
      if (agencies.length > 0 && _.intersection(agencies, stepAgency).length > 0) {
        this.routeModel.setAgency(stepAgency);
      }
    }
  },

  /**
   * Check if this route is inside of agency limit
   * @memberOf module:Layers.RouteLayer
   * @function
   * @param {number} i - The index of route in the list of routes that belong to agencies array
   */
  setLimitToAgencies(i) {
    if ((this.styles.type === 'agencyStyle' && (typeof(this.styles.applyTo) === 'number' &&
        i < this.styles.applyTo) || typeof(this.styles.applyTo) === 'string') || this.styles.applyTo === 'agency') {
      this.initialStyle = 'defaultStyle';
    }
  },

  //Create a transparentRoute with wide weight in order to handle click and hover events
  _createTransparentRoute() {
    let completeGeom = 'overview_path';
    this.transparentRoute = new google.maps.Polyline({
      options: {
        strokeOpacity: 0,
        strokeWeight: 15
      },
      geodesic: true,
      clickable: true,
      path: this.providedRoute[completeGeom]
    });
    this._bindListeners(this.transparentRoute);
  },

  _createBackgroundRoute() {
    if (this.backgroundRoute.length) {
      this.removeBackgroundRoute();
    }
    const path = 'path',
      travelmode = 'travel_mode',
      options = {
        strokeWeight: 10,
        strokeColor: '#585858'
      };
    this.providedRoute.legs[0].steps.forEach((step) => {
      if (step[travelmode] === 'TRANSIT') {
        this.backgroundRoute.push(new google.maps.Polyline({
          options: options,
          path: step[path],
          map: this.map
        }));
      }
    });
  },

  removeBackgroundRoute() {
    this.backgroundRoute.forEach((poly) => {
      poly.setMap(null);
    });
    this.backgroundRoute = [];
  },

  /**
   * Get bounding box from route
   * @memberOf module:Layers.RouteLayer
   * @function
   * @returns {LatLngBounds} The bounds
   */
  getBounds() {
    let bounds = new google.maps.LatLngBounds();
    let path = this.transparentRoute.getPath();
    for (let i = 0; i < path.getLength(); i++) {
      bounds.extend(path.getAt(i));
    }
    return bounds;
  },
  //Create a polyline for a determinated step
  _createGeoStep(path, opt) {
    let geoStep = new google.maps.Polyline({
      path: path,
      options: opt
    });
    return geoStep;
  },
  //Create the options to the possible status a determinated step
  _createOptStep(step) {
    let optStep = {
      defaultStyle: this._setStyleByType(step),
      basicStyle: this._normalizeOpt(this.styles.basicStyle),
      highlightStyle: this._normalizeOpt(this.styles.highlightStyle),
      selectedStyle: this._normalizeOpt(this.styles.selectedStyle)
    };

    return { path: step.path, options: optStep };
  },
  //Normalize the style checking if is a possible google option and adding common options
  _normalizeOpt(style) {
    let normalizedOpt = {
      geodesic: true,
      map: this.map
    };
    _.each(style, (value, name) => {
      if (this._isPolylineOption(name)) {
        normalizedOpt[name] = value;
      }
    });
    return normalizedOpt;
  },
  // Check if is a possible google option
  _isPolylineOption(name) {
    let polOpt = ['clickable', 'draggable', 'editable', 'geodesic', 'icons', 'map', 'path',
      'strokeColor', 'strokeOpacity', 'strokeWeight', 'visible', 'zIndex'
    ];
    if (polOpt.indexOf(name) !== -1) {
      return true;
    } else {
      return false;
    }
  },

  //Set default style for a step by type
  _setStyleByType(step) {
    switch (this.styles.type) {
      case 'uniqueColors':
        return this._applyUniqueColors(step);
      case 'agencyStyle':
        return this._applyAgencyStyle(step);
      case 'googleStyle':
        return this._applyGoogleStyle(step);
      case 'modesStyle':
        return this._applyModesStyle(step);
    }
  },

  //Set style to option 'uniqueColors':  list of colors that will apply for each route displayed on the map.
  _applyUniqueColors(step) {
    this.styles.defaultStyle.strokeColor = this.styles.defaultStyle.colorsList[this.index];
    let normalizedOpt = this._normalizeOpt(this.styles.defaultStyle);
    let legendText = `${this.widget.polyglot.t('legend.routeName')} ${this.index + 1}`;
    this._addElementToLegend(legendText, normalizedOpt);
    return normalizedOpt;
  },

  //Set style to option 'agencyStyle': Color for specified travel company (agencies)
  _applyAgencyStyle(step) {
    let normalizedOpt = this._normalizeOpt(this.styles.defaultStyle);
    let legendText = this.routeModel.getAgency();
    if (legendText) {
      this._addElementToLegend(legendText.toString(), normalizedOpt);
    }
    return normalizedOpt;
  },

  //Set style to option 'modesStyle':  list of colors that will apply for each transport mode (it can be styles for general modes
  _applyModesStyle(step) {
    let modeStyle, legendText;
    let modeField = 'travel_mode';
    if (step[modeField] === 'TRANSIT') {
      if (_.isUndefined(this.styles.defaultStyle[step.transit.line.vehicle.type])) {
        modeStyle = this.styles.defaultStyle.TRANSIT;
      } else {
        modeStyle = this.styles.defaultStyle[step.transit.line.vehicle.type];
      }
      legendText = step.transit.line.vehicle.name;
    } else {
      modeStyle = this.styles.defaultStyle[step[modeField]];
      legendText = step[modeField];
    }
    let normalizedOpt = this._normalizeOpt(modeStyle);
    this._addElementToLegend(legendText, normalizedOpt);
    return normalizedOpt;
  },
  //Set style to option 'googleStyle': colors that are provided by api (only transit so the rest are combined with 'modesStyle')
  _applyGoogleStyle(step) {
    let modeField = 'travel_mode';
    let modeStyle = this.styles.defaultStyle[step[modeField]];
    let legendText = step[modeField];
    if (step[modeField] === 'TRANSIT') {
      modeStyle.strokeColor = step.transit.line.color || '#000';
      legendText = step.transit.line.agencies[0].name;
    }
    let normalizedOpt = this._normalizeOpt(modeStyle);
    this._addElementToLegend(legendText, normalizedOpt);
    return normalizedOpt;
  },
  /**
   * Draw the routes on map
   * @memberOf module:Layers.RouteLayer
   * @function
   */
  draw() {
    let routeStep, zIndex;
    for (routeStep = 0; routeStep < this.optRoute.length; routeStep++) {
      let step = this._createGeoStep(this.optRoute[routeStep].path, this.optRoute[routeStep].options[this.initialStyle]);
      this.geoRoute.push(step);
      //save the zIndex in order to apply it to transparentRoute
      zIndex = this.optRoute[routeStep].options[this.initialStyle].zIndex;
    }
    this.transparentRoute.setOptions({
      map: this.map,
      zIndex: zIndex + 999
    });
    this.routeModel.setStyle(this.initialStyle);
  },
  /**
   * Remove the routes from map and its related events
   * @memberOf module:Layers.RouteLayer
   * @function
   */
  remove() {
    let routeStep;
    for (routeStep = 0; routeStep < this.geoRoute.length; routeStep++) {
      this.geoRoute[routeStep].setMap(null);
    }
    this.transparentRoute.setMap(null);
    google.maps.event.removeListener(this.clickOnMapListener);
    this.routeModel.off('change:selected');
    this.routeModel.off('change:style');
    if (this.transitStopsLayer) {
      this.transitStopsLayer.remove();
    }
    if (this.backgroundRoute && this.backgroundRoute.length) {
      this.removeBackgroundRoute();
    }
  },

  hide() {
    this.routeModel.setVisibility(false);
  },

  show() {
    this.routeModel.setVisibility(true);
  },

  setVisibilityStyle(visibility) {
    let routeStep;
    if (visibility) {
      for (routeStep = 0; routeStep < this.geoRoute.length; routeStep++) {
        this.geoRoute[routeStep].setMap(this.map);
      }
      this.transparentRoute.setMap(this.map);
    } else {
      for (routeStep = 0; routeStep < this.geoRoute.length; routeStep++) {
        this.geoRoute[routeStep].setMap(null);
      }
      this.transparentRoute.setMap(null);
    }
  },

  //Append 'hover', 'leave', 'click', 'select' and 'unselect' events to transparent Route
  _bindListeners(route) {
    if (this.routeHoverable) {
      google.maps.event.addListener(route, 'mouseover', () => {
        this.routeModel.onHover();
      });
      google.maps.event.addListener(route, 'mouseout', () => {
        this.routeModel.onLeave();
      });
    }
    if (this.routeClickable) {
      google.maps.event.addListener(route, 'click', () => {
        this.selectRoute();
      });
    }
    if (this.styles.deselectClickingOut) {
      this.clickOnMapListener = google.maps.event.addListener(this.map, 'click', () => {
        if (this.routeModel.isSelected()) {
          this.mapClick = true;
          this.deselectRoute();
        }
      });
    }
    this.routeModel.on('change:selected', (model) => {
      if (this.mapClick) {
        this.mapClick = false;
        return;
      }
      if (!model.isSelected()) {
        this.deselectRoute('basicStyle');
      } else {
        this.selectRoute();
      }
    }, this.routeModel);

    this.routeModel.on('change:style', (model) => {
      if (!model.isSelected()) {
        this._applyStyle(model.getStyle());
      }
    });

    this.routeModel.on('change:highlighted', (model) => {
      if (model.isHovered()) {
        this.highlightRoute();
      } else {
        this.leaveRoute();
      }
    });
    this.routeModel.on('change:visibility', (model) => {
      this.setVisibilityStyle(model.getVisibility());
    });
  },

  //Change the icons from a style in order to increase its size
  _getStyleWithIcons(icons) {
    let iconsStyle = {};
    if (icons) {
      let biggerIcons = [];
      for (let i = 0; i < icons.length; i++) {
        let scale = icons[i].icon.scale + 1;
        let icon = _.extend({}, icons[i].icon, { scale: scale });
        let repeat = (parseInt(icons[i].repeat.split('px')[0]) + 2) + 'px';
        let newIcon = _.extend({}, icons[i], { icon: icon, repeat: repeat });
        biggerIcons.push(newIcon);
      }
      iconsStyle = {
        strokeWeight: 0,
        strokeOpacity: 0,
        icons: biggerIcons
      };
    }
    return iconsStyle;
  },
  /**
   * Highlight the hovered route combining the highlightStyle and the previous style
   * @memberOf module:Layers.RouteLayer
   * @function
   */
  highlightRoute() {
    let currentStyle = this.routeModel.getStyle();
    let isSelected = this.routeModel.isSelected();
    if (!isSelected) {
      this._applyEventStyle('highlightStyle', currentStyle);
    }
  },
  /**
   * Set the previous style to the leaved route
   * @memberOf module:Layers.RouteLayer
   * @function
   */
  leaveRoute() {
    let currentStyle = this.routeModel.getStyle();
    let isSelected = this.routeModel.isSelected();
    if (!isSelected) {
      this._applyStyle(currentStyle);
    }
  },
  /**
   * Select the route combining the defaultStyle and the selectedStyle
   * @memberOf module:Layers.RouteLayer
   * @function
   */
  selectRoute() {
    this._applyEventStyle('selectedStyle', 'defaultStyle');
    this.routeModel.onSelect();
    if (this.transitStopsLayer) {
      this.transitStopsLayer.add();
    }

    if (this.backgroundRoute) {
      this._createBackgroundRoute();
    }
  },
  /**
   * Deselect the route and set its style like the previous (basic or default) or the specified on param
   * @memberOf module:Layers.RouteLayer
   * @function
   * @param {string} style - The style that will apply ('defaultStyle' or 'basicStyle').
   *  It's optional and if it's empty it'll apply the previous one
   */
  deselectRoute(style) {
    let prevStyle = style || this.routeModel.getStyle();
    this._applyStyle(prevStyle);
    this.routeModel.onDeselect();
    if (this.transitStopsLayer) {
      this.transitStopsLayer.remove();
    }
    if (this.backgroundRoute &&  this.backgroundRoute.length) {
      this.removeBackgroundRoute();
    }
  },
  //Apply a determinated style
  _applyStyle(style) {
    let routeStep;
    for (routeStep = 0; routeStep < this.optRoute.length; routeStep++) {
      let opt = this.optRoute[routeStep].options[style];
      delete opt.map;
      this.geoRoute[routeStep].setOptions(opt);
    }
  },
  //Apply a determinated style combining it with another
  //this style is applied for click and hover event because in these cases the style should be merge with currentStyle
  _applyEventStyle(eventStyle, currentStyle) {
    let routeStep;
    for (routeStep = 0; routeStep < this.optRoute.length; routeStep++) {
      let appliedStyle = this.optRoute[routeStep].options[currentStyle];
      let iconsStyle = this._getStyleWithIcons(appliedStyle.icons); //when the applied style has dots or another icon
      let opt = _.extend({}, appliedStyle,
        this.optRoute[routeStep].options[eventStyle], iconsStyle);
      delete opt.map;
      this.geoRoute[routeStep].setOptions(opt);
    }
  }
});



// WEBPACK FOOTER //
// ./lmm-widget/src/layers/types/RouteLayer.js